import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Badge } from '@shared/components/ui/badge';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import { Download, Eye, Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CourierFile } from '../models';

interface UseAgGridColumnsProps {
  onDownload?: (fileId: number, fileName?: string) => void;
  downloadingFileId?: number | null;
}

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy HH:mm
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

export const useAgGridColumns = ({ onDownload, downloadingFileId }: UseAgGridColumnsProps = {}) => {
  const { t } = useTranslation('codCourierFiles');
  const navigate = useNavigate();
  const currency = useCurrency();

  const columnDefs: ColDef[] = useMemo(() => {
    const columns: CustomColDef[] = [
      {
        field: 'courier.name',
        headerName: t('table.courier', 'Courier'),
        minWidth: 200,
        sortable: false,
        valueGetter: (params) => params.data?.courier?.name ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.courier?.name ?? '-'}</span>
        ),
      },
      {
        field: 'file_name',
        headerName: t('table.file_name', 'Nombre Archivo'),
        minWidth: 200,
        sortable: false,
        valueGetter: (params) => params.data?.file_name ?? '-',
        cellRenderer: (params: any) => <span className="text-sm text-foreground">{params.data?.file_name ?? '-'}</span>,
      },
      {
        field: 'file_date',
        headerName: t('table.file_date', 'Fecha Archivo'),
        minWidth: 150,
        sortable: true,
        valueGetter: (params) => params.data?.file_date ?? null,
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{formatDate(params.data?.file_date)}</span>
        ),
      },
      {
        field: 'total_declared',
        headerName: t('table.total_declared', 'Total Declarado'),
        minWidth: 150,
        sortable: true,
        valueGetter: (params) => params.data?.total_declared ?? 0,
        cellRenderer: (params: any) => {
          const amount = params.data?.total_declared ?? 0;
          return <span className="text-sm font-medium text-foreground">{formatCurrencyAmount(amount, currency)}</span>;
        },
      },
      {
        field: 'processed_orders_count',
        headerName: t('table.processed_orders_count', 'Pedidos importados'),
        minWidth: 160,
        sortable: true,
        valueGetter: (params) => params.data?.processed_orders_count ?? 0,
        cellRenderer: (params: any) => {
          const n = params.data?.processed_orders_count ?? 0;
          return (
            <span className="text-sm tabular-nums text-foreground">
              {new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(n)}
            </span>
          );
        },
      },
      {
        field: 'payments_total',
        headerName: t('table.payments_total', 'Total pagos importados'),
        minWidth: 170,
        sortable: true,
        valueGetter: (params) => params.data?.payments_total ?? 0,
        cellRenderer: (params: any) => {
          const amount = params.data?.payments_total ?? 0;
          return <span className="text-sm font-medium text-foreground">{formatCurrencyAmount(amount, currency)}</span>;
        },
      },
      {
        field: 'status',
        headerName: t('table.status', 'Estado'),
        minWidth: 130,
        sortable: false,
        valueGetter: (params) => params.data?.status ?? '-',
        cellRenderer: (params: any) => {
          const status = params.data?.status;
          if (!status) return '-';

          // Normalizar el estado a mayúsculas para la comparación
          const normalizedStatus = status.toUpperCase();

          type BadgeVariant =
            | 'transparent-orange'
            | 'transparent-green'
            | 'transparent-blue'
            | 'destructive'
            | 'default';

          const variants: Record<string, { variant: BadgeVariant; label: string }> = {
            IMPORTED: {
              variant: 'transparent-blue',
              label: t('status.imported', 'Importado'),
            },
            PROCESSED: {
              variant: 'transparent-green',
              label: t('status.processed', 'Procesado'),
            },
            ERROR: {
              variant: 'destructive',
              label: t('status.error', 'Error'),
            },
          };

          const statusInfo = variants[normalizedStatus] || { variant: 'default' as BadgeVariant, label: status };

          return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
        },
      },
      {
        field: 'imported_at',
        headerName: t('table.imported_at', 'Importado'),
        minWidth: 180,
        sortable: true,
        valueGetter: (params) => params.data?.imported_at ?? null,
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{formatDateTime(params.data?.imported_at)}</span>
        ),
      },
      {
        field: 'bank_collected_at',
        headerName: t('table.bank_collected_at', 'Fecha Cobro Banco'),
        minWidth: 180,
        sortable: true,
        valueGetter: (params) => params.data?.bank_collected_at ?? null,
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">
            {params.data?.bank_collected_at ? formatDate(params.data.bank_collected_at) : '-'}
          </span>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 100, // o el ancho que necesites
        minWidth: 100,
        maxWidth: 100,
        resizable: false,
        sortable: false,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: any) => {
          const courierFile: CourierFile = params.data;
          if (!courierFile) return null;

          const isCurrentDownloading = downloadingFileId === courierFile.id;

          // Handler para evitar que el click seleccione la fila
          const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
            e.stopPropagation();
            e.preventDefault();
            action();
          };

          return (
            <div className="flex items-center justify-center h-full gap-1" onClick={(e) => e.stopPropagation()}>
              {/* Botón de descarga */}
              {onDownload && (
                <button
                  onClick={(e) => handleButtonClick(e, () => onDownload(courierFile.id, courierFile.file_name))}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="p-1.5 hover:bg-accent rounded-md transition-colors disabled:opacity-50"
                  title={t('actions.download', 'Descargar archivo')}
                  disabled={isCurrentDownloading}
                >
                  {isCurrentDownloading ? (
                    <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              )}
              {/* Botón de ver detalle */}
              <button
                onClick={(e) => handleButtonClick(e, () => navigate(`/cod-payments/courier-files/${courierFile.id}`))}
                onMouseDown={(e) => e.stopPropagation()}
                className="p-1.5 hover:bg-accent rounded-md transition-colors"
                title={t('actions.view', 'Ver detalle')}
              >
                <Eye className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          );
        },
      },
    ];

    return columns;
  }, [t, navigate, onDownload, downloadingFileId, currency]);

  return { columnDefs };
};
